import { useState } from 'react';
import { Badge, Group } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { useReorderSuggestionsReport } from '../api';
import { formatQty } from '../format';
import type { ReorderSuggestionRow } from '../types';

/** Report #10 (BUILD_SPEC.md §13) — read-only reorder suggestions. The
 * purchasing-intake module's own Reorder tab additionally offers one-click
 * "create draft PO" (plan.md G20) — this screen is the plain filter+table+CSV
 * report template covering the same data for the Reports hub. */
export function ReorderSuggestionsReport() {
  const [siteId, setSiteId] = useState<string | null>(null);

  const query = useReorderSuggestionsReport({ siteId: siteId ?? undefined });
  const rows = query.data?.items ?? [];

  const columns: ReportColumn<ReorderSuggestionRow>[] = [
    { key: 'product', header: 'Product', render: (r) => `${r.productSku} — ${r.productName}` },
    { key: 'onHand', header: 'On hand', align: 'right', render: (r) => <Badge color="amber" variant="light">{formatQty(r.onHandQty)}</Badge> },
    { key: 'minLevel', header: 'Min level', align: 'right', render: (r) => formatQty(r.minLevel) },
    { key: 'suggested', header: 'Suggested qty', align: 'right', render: (r) => (r.suggestedQty ? formatQty(r.suggestedQty) : '—') },
    { key: 'supplier', header: 'Preferred supplier', render: (r) => r.preferredSupplierName ?? 'No supplier configured' },
  ];

  return (
    <ReportPage
      title="Reorder suggestions"
      description="Products below their configured minimum level, grouped by preferred supplier."
      action={
        <CsvDownloadButton
          path="/reporting/reorder"
          query={{ siteId: siteId ?? undefined }}
          filename="reorder-suggestions.csv"
          description="Exports the reorder suggestions for this site as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={220} />
      </Group>

      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.productId}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage="Failed to load reorder suggestions."
        emptyMessage="Nothing is below its minimum level right now"
        emptyHint="Stock levels look healthy for this site."
        emptyIcon={IconCircleCheck}
      />
    </ReportPage>
  );
}
